import { useId, useState } from "react";

import { Loading } from "../../../ui/EmptyState";
import { TextField } from "../../../ui/TextField";
import type { ModelListState } from "./useModelList";
import { MESSAGE_LIMIT } from "./TestResultView";
import { cut, TEST_RESULT_WORDS } from "./words";

/** A list longer than this gets a search box above it. */
export const SEARCH_ABOVE = 10;

interface ModelPickerProps {
  models: string[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
}

/** The models a service offers, as a drop-down. A long list can be narrowed by typing part of a name. */
export function ModelPicker({ models, value, onChange, label = "Model" }: ModelPickerProps) {
  const selectId = useId();
  const [search, setSearch] = useState("");
  const searchable = models.length > SEARCH_ABOVE;
  const needle = searchable ? search.trim().toLowerCase() : "";
  const shown = needle ? models.filter((name) => name.toLowerCase().includes(needle) || name === value) : models;
  const matching = needle ? shown.filter((name) => name.toLowerCase().includes(needle)).length : models.length;
  return (
    <div className="stack ra-model-choice">
      {searchable && (
        <TextField
          label="Search the models"
          hint={`The service offers ${models.length} models. Type part of a name to narrow the list.`}
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
      )}
      <div className="field">
        <label htmlFor={selectId} className="field__label">
          {label}
        </label>
        <select id={selectId} className="field__input" value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">Pick a model</option>
          {shown.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        {searchable && needle && (
          <p role="status" className="field__hint muted">
            {matching === 0 ? "No model matches. Change the search." : `${matching} of ${models.length} models match.`}
          </p>
        )}
      </div>
    </div>
  );
}

interface ModelChoiceProps {
  state: ModelListState;
  /** The model picked or typed so far. */
  value: string;
  onChange: (value: string) => void;
  /** Said under the drop-down: what the list is, and what picking does. */
  hint?: string;
}

/**
 * What follows the Connect button: the drop-down of the models the service offers; or, when the service gives no list, a box
 * to type the model name in, with the reason; or the plain result when the service could not be asked. Nothing while idle.
 */
export function ModelChoice({ state, value, onChange, hint }: ModelChoiceProps) {
  if (state.phase === "loading") return <Loading>Asking the service for its models</Loading>;
  if (state.phase === "listed") {
    return (
      <div className="stack">
        <ModelPicker models={state.models} value={value} onChange={onChange} />
        {hint && <p className="muted ra-model-hint">{hint}</p>}
      </div>
    );
  }
  if (state.phase === "no_list") {
    return (
      <TextField
        label="Model name"
        hint={cut(state.message.trim(), MESSAGE_LIMIT)}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete="off"
        spellCheck={false}
      />
    );
  }
  if (state.phase === "failed") {
    const headline = TEST_RESULT_WORDS[state.result].headline;
    const message = cut(state.message.trim(), MESSAGE_LIMIT);
    return (
      <p role="alert" className="form-error" data-result={state.result}>
        {headline}
        {message && message !== headline && <span className="ra-test__message muted"> {message}</span>}
      </p>
    );
  }
  if (state.phase === "refused") {
    return (
      <p role="alert" className="form-error">
        {state.message}
      </p>
    );
  }
  return null;
}
